import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { PageHead, RiskBar, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

// Biometric identity screen (part 91).
//
// The browser talks WebAuthn directly: navigator.credentials.create() for
// enrolment and navigator.credentials.get() for assertion. The server issues
// the challenge, fixes the relying-party id and origin, then verifies the ES256
// signature itself. This page never sees a private key and never decides
// whether an assertion is valid.

interface CredentialRow {
  id: string
  user_id: string
  credential_id: string
  nickname: string
  transports: string[]
  algorithm: number
  sign_count: number
  status: string
  attestation_format: string
  clone_detected_at?: string
  last_used_at?: string
  created_at: string
}

interface RiskReason {
  code: string
  message: string
  points: number
}

interface Risk {
  score: number
  level: string
  reasons: RiskReason[]
}

interface RegistrationOptions {
  challenge: string
  rp: { id: string; name: string }
  user: { id: string; name: string; display_name: string }
  pub_key_params: { type: string; alg: number }[]
  timeout_ms: number
  attestation: string
  exclude_credentials: { type: string; id: string; transports?: string[] }[]
}

interface AssertionOptions {
  challenge: string
  rp_id: string
  timeout_ms: number
  allow_credentials: { type: string; id: string; transports?: string[] }[]
  user_verification: string
}

interface FinishRegistration {
  verified: boolean
  credential: CredentialRow
  risk: Risk
}

interface FinishAssertion {
  verified: boolean
  credential_id: string
  user_id: string
  sign_count: number
  clone_warning: boolean
  user_verified: boolean
  risk: Risk
  message: string
}

const QUERY_KEY = ['biometric-credentials']

const statusLabel: Record<string, string> = {
  active: 'ใช้งานได้',
  compromised: 'ปิดใช้งาน — พบสัญญาณโคลน',
  revoked: 'เพิกถอนแล้ว',
}

const transportLabel: Record<string, string> = {
  internal: 'ในเครื่อง',
  hybrid: 'โทรศัพท์หรือแท็บเล็ต',
  usb: 'USB',
  nfc: 'NFC',
  ble: 'บลูทูธ',
  cable: 'สายเคเบิล',
}

function b64urlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const pad = '='.repeat((4 - (padded.length % 4)) % 4)
  const binary = atob(padded + pad)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function bufferToB64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function Biometric() {
  const qc = useQueryClient()
  const supported = typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined'
    && window.isSecureContext

  const q = useQuery({ queryKey: QUERY_KEY, queryFn: () => api<CredentialRow[]>('/v1/biometric/credentials') })
  const [nickname, setNickname] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [assertion, setAssertion] = useState<FinishAssertion | null>(null)
  const [cloneWarning, setCloneWarning] = useState<string | null>(null)

  const register = useMutation({
    mutationFn: async (label: string): Promise<FinishRegistration> => {
      const options = await apiWrite<RegistrationOptions>('/v1/biometric/registration/begin', 'POST', {})
      const created = await navigator.credentials.create({
        publicKey: {
          challenge: b64urlToBuffer(options.challenge),
          rp: options.rp,
          user: {
            id: new TextEncoder().encode(options.user.id),
            name: options.user.name,
            displayName: options.user.display_name,
          },
          pubKeyCredParams: options.pub_key_params.map((p) => ({ type: 'public-key' as const, alg: p.alg })),
          timeout: options.timeout_ms,
          attestation: 'none',
          authenticatorSelection: { userVerification: 'required', residentKey: 'preferred' },
          excludeCredentials: options.exclude_credentials.map((c) => ({
            type: 'public-key' as const,
            id: b64urlToBuffer(c.id),
            transports: (c.transports ?? []) as AuthenticatorTransport[],
          })),
        },
      })
      if (!created || created.type !== 'public-key') {
        throw new Error('อุปกรณ์ไม่ได้สร้างข้อมูลยืนยันตัวตน')
      }
      const credential = created as PublicKeyCredential
      const response = credential.response as AuthenticatorAttestationResponse
      return apiWrite<FinishRegistration>('/v1/biometric/registration/finish', 'POST', {
        challenge: options.challenge,
        credential_id: bufferToB64url(credential.rawId),
        attestation_object: bufferToB64url(response.attestationObject),
        client_data_json: bufferToB64url(response.clientDataJSON),
        transports: response.getTransports ? response.getTransports() : [],
        nickname: label,
      })
    },
    onSuccess: (res) => {
      setError(null)
      setNickname('')
      setNotice(`ลงทะเบียน “${res.credential.nickname}” สำเร็จ — กุญแจสาธารณะถูกเก็บฝั่งเซิร์ฟเวอร์แล้ว`)
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (e) => {
      setNotice(null)
      setError(errorText(e, 'ลงทะเบียนอุปกรณ์ไม่สำเร็จ'))
    },
  })

  const assert = useMutation({
    mutationFn: async (): Promise<FinishAssertion> => {
      const options = await apiWrite<AssertionOptions>('/v1/biometric/assertion/begin', 'POST', {})
      const asserted = await navigator.credentials.get({
        publicKey: {
          challenge: b64urlToBuffer(options.challenge),
          rpId: options.rp_id,
          timeout: options.timeout_ms,
          userVerification: 'required',
          allowCredentials: options.allow_credentials.map((c) => ({
            type: 'public-key' as const,
            id: b64urlToBuffer(c.id),
            transports: (c.transports ?? []) as AuthenticatorTransport[],
          })),
        },
      })
      if (!asserted || asserted.type !== 'public-key') {
        throw new Error('อุปกรณ์ไม่ได้ส่งผลการยืนยันตัวตน')
      }
      const credential = asserted as PublicKeyCredential
      const response = credential.response as AuthenticatorAssertionResponse
      return apiWrite<FinishAssertion>('/v1/biometric/assertion/finish', 'POST', {
        challenge: options.challenge,
        credential_id: bufferToB64url(credential.rawId),
        authenticator_data: bufferToB64url(response.authenticatorData),
        client_data_json: bufferToB64url(response.clientDataJSON),
        signature: bufferToB64url(response.signature),
        user_handle: response.userHandle ? bufferToB64url(response.userHandle) : '',
      })
    },
    onSuccess: (res) => {
      setError(null)
      setCloneWarning(null)
      setAssertion(res)
      setNotice(res.message)
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onError: (e) => {
      setNotice(null)
      setAssertion(null)
      const message = errorText(e, 'ยืนยันตัวตนไม่สำเร็จ')
      if (message.includes('โคลน') || message.includes('ตัวนับลายเซ็น')) {
        setCloneWarning(message)
      } else {
        setError(message)
      }
    },
  })

  const rows = q.data ?? []
  const active = rows.filter((row) => row.status === 'active')
  const busy = register.isPending || assert.isPending

  return (
    <>
      <PageHead
        title="ไบโอเมตริกและพาสคีย์"
        sub="ลงทะเบียนอุปกรณ์และยืนยันตัวตนด้วยลายเซ็น ES256 · เซิร์ฟเวอร์ตรวจลายเซ็นและตรวจจับอุปกรณ์โคลน"
        actions={<span className="chip">{rows.length} อุปกรณ์ · ใช้งานได้ {active.length}</span>}
      />

      {!supported ? (
        <div className="card card-pad" style={{ marginBottom: 14 }}>
          <div className="form-error">
            เบราว์เซอร์นี้ไม่รองรับ WebAuthn หรือหน้านี้ไม่ได้เปิดผ่าน HTTPS/localhost
            จึงยังลงทะเบียนอุปกรณ์ไม่ได้ — เปิดผ่าน HTTPS หรือ localhost แล้วลองใหม่
          </div>
        </div>
      ) : null}

      {notice ? (
        <div className="card card-pad" style={{ marginBottom: 14, borderColor: 'var(--color-emerald)' }}>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span className="sev sev-low"><i className="sev-dot" />สำเร็จ</span>
            <span style={{ fontSize: 12.5 }}>{notice}</span>
          </div>
        </div>
      ) : null}

      {cloneWarning ? (
        <div className="card card-pad" style={{ marginBottom: 14, borderColor: 'var(--color-crimson)' }}>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <span className="sev sev-critical"><i className="sev-dot" />อุปกรณ์ถูกปิดใช้งาน</span>
            <span style={{ fontSize: 12.5 }}>{cloneWarning}</span>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="card card-pad" style={{ marginBottom: 14 }}>
          <div className="form-error">{error}</div>
        </div>
      ) : null}

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">ลงทะเบียนอุปกรณ์ใหม่</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            ตั้งชื่อที่จดจำได้ เช่น “แล็ปท็อปที่ทำงาน” หรือ “โทรศัพท์ส่วนตัว”
            แล้วยืนยันด้วยลายนิ้วมือ ใบหน้า หรือ PIN ของอุปกรณ์
            กุญแจส่วนตัวจะไม่ถูกส่งออกจากอุปกรณ์
          </p>
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="bio-nickname">ชื่ออุปกรณ์</label>
            <input
              id="bio-nickname"
              className="input"
              maxLength={80}
              placeholder="เช่น โทรศัพท์ส่วนตัว"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
            />
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 16 }}
            disabled={!supported || busy}
            onClick={() => register.mutate(nickname.trim())}
          >
            {register.isPending ? 'กำลังรออุปกรณ์ยืนยัน…' : 'ลงทะเบียนอุปกรณ์'}
          </button>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.8 }}>
            ลงทะเบียนได้หลายอุปกรณ์ต่อผู้ใช้ และเพิกถอนได้ภายหลังเมื่ออุปกรณ์สูญหาย
          </div>
        </div>

        <div className="card card-pad">
          <div className="card-label">ทดสอบยืนยันตัวตน</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            ระบบจะออกคำท้าทายอายุ 5 นาที ใช้อุปกรณ์ที่ลงทะเบียนไว้ลงนาม
            แล้วเซิร์ฟเวอร์ตรวจลายเซ็นด้วยกุญแจสาธารณะที่จัดเก็บไว้
          </p>
          <button
            className="btn"
            style={{ marginTop: 16 }}
            disabled={!supported || busy || active.length === 0}
            onClick={() => assert.mutate()}
          >
            {assert.isPending ? 'กำลังรออุปกรณ์ยืนยัน…' : 'ยืนยันตัวตนด้วยอุปกรณ์'}
          </button>
          {active.length === 0 ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 12 }}>
              ยังไม่มีอุปกรณ์ที่ใช้งานได้ — ลงทะเบียนอุปกรณ์ก่อนจึงจะทดสอบได้
            </div>
          ) : null}

          {assertion ? (
            <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                <span className="sev sev-low"><i className="sev-dot" />ยืนยันสำเร็จ</span>
                <span className="mono dim" style={{ fontSize: 11 }}>
                  {assertion.credential_id.slice(0, 16)}…
                </span>
              </div>
              <div className="row" style={{ marginTop: 12, gap: 18 }}>
                <div>
                  <div className="card-label">ตัวนับลายเซ็น</div>
                  <div className="tnum" style={{ fontSize: 20 }}>{assertion.sign_count}</div>
                </div>
                <div>
                  <div className="card-label">ผู้ใช้ยืนยันแล้ว</div>
                  <div style={{ fontSize: 20 }}>{assertion.user_verified ? 'ใช่' : 'ไม่'}</div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <div className="card-label">ความเสี่ยง</div>
                  <RiskBar score={assertion.risk.score} />
                </div>
              </div>
              {assertion.risk.reasons.length > 0 ? (
                <ul className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
                  {assertion.risk.reasons.map((reason) => (
                    <li key={reason.code}>{reason.message} ({reason.points > 0 ? '+' : ''}{reason.points})</li>
                  ))}
                </ul>
              ) : (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                  ไม่พบประวัติที่น่าเป็นห่วงในช่วง 24 ชั่วโมง
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="card-label">อุปกรณ์ที่ลงทะเบียนไว้</span>
          <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} รายการ</span>
        </div>

        {q.isLoading ? <Skel h={180} /> : q.isError ? (
          <div className="card card-pad">
            <div className="form-error">โหลดรายการอุปกรณ์ไม่สำเร็จ — ตรวจสอบว่าเส้นทาง /v1/biometric/credentials ถูก mount แล้ว</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="card card-pad dim" style={{ fontSize: 12.5 }}>
            ยังไม่มีอุปกรณ์ — ตั้งชื่อแล้วกด “ลงทะเบียนอุปกรณ์” เพื่อเริ่มใช้งานพาสคีย์
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>ชื่ออุปกรณ์</th><th>รหัสอุปกรณ์</th><th>การเชื่อมต่อ</th>
                  <th>ตัวนับ</th><th>ใช้ล่าสุด</th><th>ลงทะเบียนเมื่อ</th><th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} style={{ cursor: 'default' }}>
                    <td>{row.nickname || 'อุปกรณ์ไม่มีชื่อ'}</td>
                    <td className="mono dim" style={{ fontSize: 11 }}>{row.credential_id.slice(0, 18)}…</td>
                    <td>
                      <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {row.transports.length === 0
                          ? <span className="chip">ไม่ระบุ</span>
                          : row.transports.map((t) => <span key={t} className="chip">{transportLabel[t] ?? t}</span>)}
                      </span>
                    </td>
                    <td className="tnum">{row.sign_count}</td>
                    <td className="dim">{row.last_used_at ? thaiDateTime(row.last_used_at) : 'ยังไม่เคยใช้'}</td>
                    <td className="dim">{thaiDateTime(row.created_at)}</td>
                    <td>
                      <span className={`chip${row.status === 'active' ? ' gold' : ''}`}>
                        {statusLabel[row.status] ?? row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">การป้องกันการปลอมตัว</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>คำท้าทายสุ่ม 256 บิต อายุ 5 นาที และใช้ได้ครั้งเดียว — การเล่นซ้ำจะถูกปฏิเสธที่ฐานข้อมูล</li>
          <li>ลายเซ็น ES256 ตรวจฝั่งเซิร์ฟเวอร์ด้วยกุญแจสาธารณะของอุปกรณ์ ไม่ใช่การเชื่อมต่อว่า “สำเร็จ” จากไคลเอนต์</li>
          <li>คำท้าทายผูกกับ RP ID และ origin ที่ออกให้ จึงย้ายพิธีไปโดเมนอื่นไม่ได้</li>
          <li>อุปกรณ์ผูกกับผู้ใช้และองค์กร — นำอุปกรณ์ของผู้อื่นมาใช้จะถูกปฏิเสธก่อนถึงขั้นตรวจลายเซ็น</li>
          <li>ตัวนับลายเซ็นต้องเพิ่มขึ้นทุกครั้ง ถอยหลังหรือเท่าเดิมจะถูกทำเครื่องหมายว่าอาจถูกโคลนและปิดใช้งานทันที</li>
          <li>คะแนนความเสี่ยงคำนวณจากประวัติที่จัดเก็บจริง ไม่ใช่ตัวนับในหน่วยความจำที่รีสตาร์ตแล้วหายไป</li>
        </ul>
      </div>
    </>
  )
}
